// src/pages/TestUtilsPage.jsx
import React, { useState } from "react";
import useAuthStore from "../../stores/authStore";

const API_URL = process.env.REACT_APP_API_URL;

const TestUtilsPage = () => {
  const { setUser, clearUser } = useAuthStore();

  const [userDetails, setUserDetails] = useState(null);
  const [quotaDetails, setQuotaDetails] = useState(null);
  const [featureName, setFeatureName] = useState("");
  const [featureUsage, setFeatureUsage] = useState(null);

  const [loadingUser, setLoadingUser] = useState(false);
  const [loadingQuota, setLoadingQuota] = useState(false);
  const [loadingUsage, setLoadingUsage] = useState(false);

  const [errorUser, setErrorUser] = useState(null);
  const [errorQuota, setErrorQuota] = useState(null);
  const [errorUsage, setErrorUsage] = useState(null);

  // Fetch /auth/me
  const fetchUserDetails = async () => {
    setLoadingUser(true);
    setErrorUser(null);
    try {
      const response = await fetch(`${API_URL}/auth/me`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUserDetails(data);
        setUser(data);
      } else {
        setErrorUser("Unauthorized or session expired");
        clearUser();
      }
    } catch (err) {
      console.error(err);
      setErrorUser("Something went wrong");
      clearUser();
    } finally {
      setLoadingUser(false);
    }
  };

  // Fetch /api/subscriptions/me/quota
  const fetchQuotaDetails = async () => {
    setLoadingQuota(true);
    setErrorQuota(null);
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/me/quota`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setQuotaDetails(data);
        // Automatically set first feature if available
        const features = Object.keys(data.plan_features || {});
        if (features.length > 0) setFeatureName(features[0]);
      } else {
        setErrorQuota("Failed to fetch quota");
      }
    } catch (err) {
      console.error(err);
      setErrorQuota("Something went wrong");
    } finally {
      setLoadingQuota(false);
    }
  };

  // Fetch /api/subscriptions/me/usage/{feature}
  const fetchFeatureUsage = async () => {
    if (!featureName) return;
    setLoadingUsage(true);
    setErrorUsage(null);
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/me/usage/${featureName}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setFeatureUsage(data);
      } else {
        setErrorUsage("Failed to fetch feature usage");
      }
    } catch (err) {
      console.error(err);
      setErrorUsage("Something went wrong");
    } finally {
      setLoadingUsage(false);
    }
  };

  const featureOptions = quotaDetails ? Object.keys(quotaDetails.plan_features || {}) : [];

  return (
    <div style={{ padding: "20px" }}>
      <h2>Test Utils Page</h2>

      {/* User Test */}
      <div style={{ marginBottom: "30px" }}>
        <h3>1. User Details (/auth/me)</h3>
        <button onClick={fetchUserDetails} disabled={loadingUser}>
          {loadingUser ? "Fetching..." : "Fetch User"}
        </button>
        {errorUser && <p style={{ color: "red" }}>{errorUser}</p>}
        {userDetails && (
          <pre style={{ marginTop: "10px", padding: "10px", background: "#f5f5f5", borderRadius: "8px" }}>
            {JSON.stringify(userDetails, null, 2)}
          </pre>
        )}
      </div>

      {/* Quota Test */}
      <div style={{ marginBottom: "30px" }}>
        <h3>2. Subscription Quota (/api/subscriptions/me/quota)</h3>
        <button onClick={fetchQuotaDetails} disabled={loadingQuota}>
          {loadingQuota ? "Fetching..." : "Fetch Quota"}
        </button>
        {errorQuota && <p style={{ color: "red" }}>{errorQuota}</p>}
        {quotaDetails && (
          <pre style={{ marginTop: "10px", padding: "10px", background: "#f5f5f5", borderRadius: "8px" }}>
            {JSON.stringify(quotaDetails, null, 2)}
          </pre>
        )}
      </div>

      {/* Feature Usage Test */}
      <div>
        <h3>3. Feature Usage (/api/subscriptions/me/usage/{'{feature}'})</h3>
        <select
          value={featureName}
          onChange={(e) => setFeatureName(e.target.value)}
          style={{ marginRight: "10px" }}
        >
          {featureOptions.length === 0 && <option value="">No features available</option>}
          {featureOptions.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
        <button onClick={fetchFeatureUsage} disabled={loadingUsage || !featureName}>
          {loadingUsage ? "Fetching..." : "Fetch Usage"}
        </button>
        {errorUsage && <p style={{ color: "red" }}>{errorUsage}</p>}
        {featureUsage && (
          <pre style={{ marginTop: "10px", padding: "10px", background: "#f5f5f5", borderRadius: "8px" }}>
            {JSON.stringify(featureUsage, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
};

export default TestUtilsPage;
